import type { RelatedEntity } from './entity-utils';

export interface EntityReferenceEdge {
  sourceType: string;
  sourceId: string;
  sourceDocumentId: string | null;
  targetType: string;
  targetId: string;
  targetDocumentId: string | null;
}

export interface RelatedEntityLookupItem {
  id: string;
  type: string;
  label: string;
  documentId?: string;
}

export interface RelatedEntitiesGroups {
  linksTo: RelatedEntity[];
  referencedIn: RelatedEntity[];
}

export interface HeadingManualChapterLink {
  headingTitle: string;
  manualChapterId: string;
}

export interface ChapterWithManualMatch {
  title: string;
  isManual?: boolean;
  analysis?: {
    manualChapterMatch?: {
      matchedManualChapterId: string | null;
    } | null;
  } | null;
}

function dedupeRelatedEntities(entities: RelatedEntity[]): RelatedEntity[] {
  const seen = new Set<string>();
  return entities.filter((entity) => {
    const key = `${entity.type}:${entity.id}:${entity.documentId ?? ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function getRelatedEntitiesFor(
  entityType: string,
  entityId: string,
  documentId: string,
  entityRefs: EntityReferenceEdge[],
  mentionableEntities: RelatedEntityLookupItem[],
): RelatedEntitiesGroups {
  const entityLookup = new Map<string, RelatedEntityLookupItem>();
  for (const entity of mentionableEntities) {
    entityLookup.set(`${entity.type}:${entity.id}:${entity.documentId ?? ''}`, entity);
  }

  const linksTo: RelatedEntity[] = [];
  const referencedIn: RelatedEntity[] = [];

  for (const ref of entityRefs) {
    if (
      ref.sourceType === entityType
      && ref.sourceId === entityId
      && ref.sourceDocumentId === documentId
    ) {
      const key = `${ref.targetType}:${ref.targetId}:${ref.targetDocumentId ?? ''}`;
      const entity = entityLookup.get(key);
      if (entity) {
        linksTo.push({
          id: ref.targetId,
          type: ref.targetType,
          label: entity.label,
          documentId: ref.targetDocumentId ?? undefined,
        });
      }
    }
    if (
      ref.targetType === entityType
      && ref.targetId === entityId
      && ref.targetDocumentId === documentId
    ) {
      const key = `${ref.sourceType}:${ref.sourceId}:${ref.sourceDocumentId ?? ''}`;
      const entity = entityLookup.get(key);
      if (entity) {
        referencedIn.push({
          id: ref.sourceId,
          type: ref.sourceType,
          label: entity.label,
          documentId: ref.sourceDocumentId ?? undefined,
        });
      }
    }
  }

  return {
    linksTo: dedupeRelatedEntities(linksTo),
    referencedIn: dedupeRelatedEntities(referencedIn),
  };
}

export function buildReferencedInByTargetId(
  targetType: string,
  documentId: string | null,
  entityRefs: EntityReferenceEdge[],
  mentionableEntities: RelatedEntityLookupItem[],
): Map<string, RelatedEntity[]> {
  const entityLookup = new Map<string, RelatedEntityLookupItem>();
  for (const entity of mentionableEntities) {
    entityLookup.set(`${entity.type}:${entity.id}:${entity.documentId ?? ''}`, entity);
  }

  const byTargetId = new Map<string, RelatedEntity[]>();
  for (const ref of entityRefs) {
    if (ref.targetType !== targetType || ref.targetDocumentId !== documentId) {
      continue;
    }
    const key = `${ref.sourceType}:${ref.sourceId}:${ref.sourceDocumentId ?? ''}`;
    const entity = entityLookup.get(key);
    if (!entity) {
      continue;
    }
    const existing = byTargetId.get(ref.targetId) ?? [];
    existing.push({
      id: ref.sourceId,
      type: ref.sourceType,
      label: entity.label,
      documentId: ref.sourceDocumentId ?? undefined,
    });
    byTargetId.set(ref.targetId, existing);
  }

  for (const [targetId, entities] of byTargetId) {
    byTargetId.set(targetId, dedupeRelatedEntities(entities));
  }
  return byTargetId;
}

export function findUserLinkedManualChapterIdForHeading(
  headingTitle: string,
  headingManualChapterLinks: HeadingManualChapterLink[],
): string | null {
  const normalizedTitle = headingTitle.trim();
  if (!normalizedTitle) {
    return null;
  }
  const link = headingManualChapterLinks.find(
    (item) => item.headingTitle.trim() === normalizedTitle,
  );
  return link?.manualChapterId ?? null;
}

export function findAnalysisMatchedManualChapterIdForHeading(
  headingTitle: string,
  chapters: ChapterWithManualMatch[],
): string | null {
  const normalizedTitle = headingTitle.trim();
  if (!normalizedTitle) {
    return null;
  }
  const extractedChapter = chapters.find(
    (chapter) => !chapter.isManual && chapter.title.trim() === normalizedTitle,
  );
  return extractedChapter?.analysis?.manualChapterMatch?.matchedManualChapterId || null;
}

export function findEffectiveManualChapterIdForHeading(
  headingTitle: string,
  chapters: ChapterWithManualMatch[],
  headingManualChapterLinks: HeadingManualChapterLink[] = [],
): string | null {
  return (
    findUserLinkedManualChapterIdForHeading(headingTitle, headingManualChapterLinks)
    ?? findAnalysisMatchedManualChapterIdForHeading(headingTitle, chapters)
  );
}
